import React, { useState } from 'react';
import { Card, Badge, Button, Input, Checkbox } from '../ui';
import { translateStatus } from '../../lib/utils';
import { useUpdateItem } from '../../hooks';
import { ChevronRight, CreditCard, Pencil, ArrowRight, Share2 } from 'lucide-react';
import { DEFAULT_USD_GEL_RATE } from '../../lib/constants';

const statusColors = {
  ordered: { bg: 'bg-gradient-to-r from-blue-500 to-blue-600', border: 'border-l-blue-500' },
  at_carrier: { bg: 'bg-gradient-to-r from-orange-400 to-orange-600', border: 'border-l-orange-500' },
  shipped: { bg: 'bg-gradient-to-r from-yellow-400 to-amber-500', border: 'border-l-yellow-500' },
  arrived: { bg: 'bg-gradient-to-r from-purple-400 to-purple-600', border: 'border-l-purple-500' },
  at_home: { bg: 'bg-gradient-to-r from-teal-400 to-teal-600', border: 'border-l-teal-500' },
  delivered_to_client: { bg: 'bg-gradient-to-r from-emerald-400 to-emerald-600', border: 'border-l-emerald-500' }
};

const platformConfig = {
  Amazon: { bg: 'bg-yellow-500', icon: '🟨' },
  Shein: { bg: 'bg-pink-500', icon: '💗' },
  'TikTok': { bg: 'bg-black', icon: '🎵' },
  'TikTok Shop': { bg: 'bg-black', icon: '🎵' },
  eBay: { bg: 'bg-blue-600', icon: '🛒' },
  AliExpress: { bg: 'bg-red-600', icon: '🔴' },
  Temu: { bg: 'bg-orange-500', icon: '🟠' },
  Etsy: { bg: 'bg-orange-400', icon: '🧡' },
  Instagram: { bg: 'bg-gradient-to-r from-purple-500 to-pink-500', icon: '📸' }
};

const getPaymentProgress = (item) => {
  if (!item.purchase_price_gel && !item.purchase_price_usd) return 0;
  const total = item.purchase_price_gel || item.purchase_price_usd * DEFAULT_USD_GEL_RATE;
  const paid = item.paid_amount_gel || 0;
  return Math.min(100, (paid / total) * 100);
};

const getPaymentGradient = (progress) => {
  if (progress >= 100) return 'bg-gradient-to-r from-green-400 to-emerald-500';
  if (progress > 0) return 'bg-gradient-to-r from-yellow-400 to-amber-500';
  return 'bg-gradient-to-r from-red-400 to-red-500';
};

export default function ItemCard({ item, account, onNextStatus, onPay, onEdit, isSelected, onSelect }) {
  const updateItemMutation = useUpdateItem();
  const [isEditingTracking, setIsEditingTracking] = useState(false);
  const [trackingCode, setTrackingCode] = useState(item.tracking_code || '');

  const profit = item.sale_price_gel && item.purchase_price_gel
    ? item.sale_price_gel - item.purchase_price_gel
    : item.sale_price_gel && item.purchase_price_usd
      ? item.sale_price_gel - item.purchase_price_usd * DEFAULT_USD_GEL_RATE
      : 0;

  const paymentProgress = getPaymentProgress(item);
  const platformStyle = platformConfig[item.platform] || { bg: 'bg-gray-500', icon: '📦' };
  const statusStyle = statusColors[item.status] || statusColors.ordered;

  const handleSaveTracking = async () => {
    if (trackingCode !== item.tracking_code) {
      await updateItemMutation.mutateAsync({ id: item.id, data: { tracking_code: trackingCode } });
    }
    setIsEditingTracking(false);
  };

  const handleTrackingKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSaveTracking();
    } else if (e.key === 'Escape') {
      setTrackingCode(item.tracking_code || '');
      setIsEditingTracking(false);
    }
  };

  const handleSelect = (checked) => {
    onSelect?.(item.id, checked);
  };

  return (
    <Card className={`mb-3 rounded-2xl shadow-md border-0 overflow-hidden card-hover border-l-4 ${statusStyle.border} transition-all duration-200 ${isSelected ? 'ring-2 ring-pink-500 bg-pink-50/30 dark:bg-pink-900/20' : ''}`}>
      <div className="p-4">
        {/* Header: Checkbox, Name, Platform Badge, Item Number */}
        <div className="flex justify-between items-start mb-3">
          <div className="flex items-center gap-2 flex-1 min-w-0 pr-3">
            <Checkbox
              checked={isSelected}
              onCheckedChange={handleSelect}
              id={`select-card-${item.id}`}
              className="shrink-0"
            />
            <div className="min-w-0">
              <div className="font-bold text-base truncate">
                {item.name}
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                #{item.item_number}
              </div>
            </div>
          </div>
          <Badge className={`${platformStyle.bg} text-white rounded-full px-3 py-1 text-xs font-medium flex items-center gap-1 shrink-0`}>
            <span>{platformStyle.icon}</span>
            {item.platform}
          </Badge>
        </div>

        {/* Price Info */}
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-3 mb-3">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-600">USD:</span>
              <span className="font-bold text-gray-900">{item.purchase_price_usd}</span>
            </div>
            <ArrowRight className="h-4 w-4 text-gray-400" />
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-600">GEL:</span>
              <span className="font-bold text-green-600">{item.sale_price_gel}</span>
            </div>
          </div>
          <div className="text-sm">
            <span className="text-muted-foreground">მოგება: </span>
            <span className={`font-semibold ${profit >= 0 ? 'text-green-600' : 'text-red-500'}`}>
              {profit.toFixed(2)} GEL
            </span>
          </div>
        </div>

        {/* Client & Account Info */}
        <div className="space-y-1.5 mb-3 text-sm text-muted-foreground">
          {item.client_name && (
            <div className="flex items-center gap-2">
              <span className="text-lg">👤</span>
              <span>{item.client_name}</span>
            </div>
          )}
          {account && (
            <div className="flex items-center gap-2">
              <span className="text-lg">🏢</span>
              <span>{account.person_name} @ {account.shipping_company_name}</span>
            </div>
          )}
        </div>

        {/* Status Pill */}
        <div className="mb-3">
          <Badge className={`${statusStyle.bg} text-white rounded-full px-4 py-1.5 text-sm font-medium shadow-sm`}>
            {translateStatus(item.status)}
          </Badge>
        </div>

        {/* Tracking */}
        <div className="mb-3">
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">ტრეკინგი:</span>
            {isEditingTracking ? (
              <Input
                className="h-7 text-xs py-1 rounded-lg"
                value={trackingCode}
                onChange={(e) => setTrackingCode(e.target.value)}
                onBlur={handleSaveTracking}
                onKeyDown={handleTrackingKeyDown}
                autoFocus
              />
            ) : (
              <span 
                className="text-xs text-pink-600 cursor-pointer hover:underline font-medium"
                onClick={() => setIsEditingTracking(true)}
              >
                {item.tracking_code || '+ დაამატე'}
              </span>
            )}
          </div>
        </div>

        {/* Payment Progress */}
        <div className="mb-4">
          <div className="flex justify-between text-xs text-muted-foreground mb-1.5">
            <span className="font-medium">გადახდა</span>
            <span className="font-bold">{Math.round(paymentProgress)}%</span>
          </div>
          <div className="h-2.5 bg-gray-200 rounded-full overflow-hidden">
            <div
              className={`h-full ${getPaymentGradient(paymentProgress)} transition-all duration-500 rounded-full`}
              style={{ width: `${paymentProgress}%` }}
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button 
            className="flex-1 h-12 rounded-xl bg-gradient-to-r from-pink-500 to-rose-500 text-white flex items-center justify-center gap-2 shadow-md hover:shadow-lg transform hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 touch-manipulation"
            onClick={() => onNextStatus(item)}
            aria-label="შემდეგი სტატუსი"
          >
            <span className="text-lg">→</span>
            <span className="text-sm font-medium">სტატუსი</span>
          </button>
          <button 
            className="w-12 h-12 rounded-xl bg-gradient-to-r from-amber-400 to-orange-500 text-white flex items-center justify-center shadow-md hover:shadow-lg transform hover:scale-105 active:scale-95 transition-all duration-200 touch-manipulation"
            onClick={() => onPay(item)}
            aria-label="გადახდა"
          >
            <CreditCard className="h-5 w-5" />
          </button>
          <button 
            className="w-12 h-12 rounded-xl bg-white border-2 border-gray-200 text-gray-600 flex items-center justify-center hover:border-pink-300 hover:text-pink-500 transform hover:scale-105 active:scale-95 transition-all duration-200 touch-manipulation"
            onClick={() => onEdit(item)}
            aria-label="რედაქტირება"
          >
            <Pencil className="h-4 w-4" />
          </button>
          <button 
            className="w-12 h-12 rounded-xl bg-gradient-to-r from-green-400 to-green-600 text-white flex items-center justify-center shadow-md hover:shadow-lg transform hover:scale-105 active:scale-95 transition-all duration-200 touch-manipulation"
            onClick={() => {
              const text = `შენი ნივთი "${item.name}" მზადაა! 🎉\n— Deals Manager`;
              if (navigator.share) {
                navigator.share({ title: 'Deals Manager', text }).catch(() => {});
              } else {
                window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
              }
            }}
            aria-label="გაზიარება"
          >
            <Share2 className="h-4 w-4" />
          </button>
        </div>
      </div>
    </Card>
  );
}